import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Brain, BookOpen, Search, Eye, Building, Mail, Users, Film, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import { StandardButton } from "@/components/ui/standard-button";
import { useGSAPAnimations } from "@/hooks/useGSAPAnimations";
import ContactModal from "@/components/ContactModal";
import ContributionButton from "@/components/ContributionButton";
import { SEOHead } from "@/components/SEOHead";
import leafMark from "@/assets/leaf-mark.png";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { NaturalLanguageSearchBar, SearchResultsView, SearchFilters, QuerySuggestions } from "@/components/search";
import { semanticSearch, SemanticSearchResult, SearchFilters as SearchFiltersType } from "@/services/semanticSearchService";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { CommunityModal } from "@/components/CommunityModal";
import { ReadingVibeOverlay } from "@/components/ReadingVibeOverlay";
import Tesseract from "@/components/Tesseract";

// Type for passing book data to Signal Archive
export interface SpotlightBook {
  id: string;
  title: string;
  author: string;
  coverUrl: string;
  description: string;
  sourceType: string;
  metadata?: Record<string, unknown>;
}

const Discovery: React.FC = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCommunityModal, setShowCommunityModal] = useState(false);
  const [searchResults, setSearchResults] = useState<SemanticSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFiltersType>({});
  const [responseTimeMs, setResponseTimeMs] = useState<number | undefined>();
  const [hasSearched, setHasSearched] = useState(false);
  
  const { mainContainerRef, heroTitleRef, addFeatureBlockRef } = useGSAPAnimations();
  const { toast } = useEnhancedToast();
  const navigate = useNavigate();

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setSearchQuery(query);
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const response = await semanticSearch(query, {
        limit: 20,
        threshold: 0.3,
        filters: searchFilters,
        searchType: 'hybrid',
      });
      
      setSearchResults(response.results);
      setResponseTimeMs(response.responseTimeMs);
    } catch (error) {
      console.error('Search failed:', error);
      // Not "destructive". A red slab across the top of the page is the wrong
      // weight for a search that did not return — nothing was lost or broken,
      // and the empty state below already says there is nothing to show. Since
      // the edge function now degrades to keyword search, reaching here means
      // both paths failed, which is worth saying once, quietly.
      toast({
        title: "Couldn't complete that search",
        description: "Give it another go in a moment.",
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchFilters, toast]);

  const handleResultClick = useCallback((result: SemanticSearchResult) => {
    // Check if this is a user's transmission (can be highlighted directly)
    const isTransmission = result.sourceType === 'transmission';
    
    let transmissionId: string | null = null;
    
    if (isTransmission) {
      // Check metadata first (most reliable for embeddings-based search)
      if (result.metadata?.transmission_id) {
        transmissionId = String(result.metadata.transmission_id);
      }
      
      // Check result.id for "transmission-123" format
      if (!transmissionId) {
        const idMatch = result.id?.match(/transmission-(\d+)/);
        if (idMatch) {
          transmissionId = idMatch[1];
        }
      }
      
      // Check bookIdentifier for "transmission_123" format
      if (!transmissionId) {
        const identifierMatch = result.bookIdentifier?.match(/transmission_(\d+)/);
        if (identifierMatch) {
          transmissionId = identifierMatch[1];
        }
      }
    }
    
    if (transmissionId && isTransmission) {
      // Navigate with highlight ID for existing transmissions
      navigate(`/book-browser?highlight=${transmissionId}&search=${encodeURIComponent(result.title)}`);
    } else {
      // For NEW books (author_books, publisher_books), store in sessionStorage and pass key in URL
      // This avoids React navigation state race conditions
      const spotlightKey = `spotlight_${Date.now()}`;
      const spotlightBook: SpotlightBook = {
        id: result.id,
        title: result.title,
        author: result.author,
        coverUrl: result.metadata?.cover_url as string || '',
        description: result.metadata?.description as string || result.metadata?.editorial_note as string || '',
        sourceType: result.sourceType,
        metadata: result.metadata,
      };
      
      console.log('Storing spotlight book in sessionStorage:', spotlightBook.title, 'key:', spotlightKey);
      sessionStorage.setItem(spotlightKey, JSON.stringify(spotlightBook));
      navigate(`/book-browser?spotlight=${spotlightKey}`);
    }
  }, [navigate]);

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Header />
        
        <main id="main-content" ref={mainContainerRef} className="container mx-auto px-6 py-12">
          <div ref={addFeatureBlockRef} className="feature-block text-center mb-16">
            <h1 ref={heroTitleRef} className="text-4xl font-light text-slate-200 mb-4 tracking-wide">
              <span className="inline-flex items-center justify-center gap-3">
                <span className="text-blue-400">LEAFNODE</span>
                <img src={leafMark} alt="" aria-hidden="true" className="h-9 md:h-10 w-auto" />
              </span>
            </h1>
            <p className="text-sm text-slate-400 mb-2">for the future-literate</p>
            
            <div className="flex items-center justify-center space-x-2 mb-8">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <div className="w-2 h-2 bg-blue-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
              <div className="w-2 h-2 bg-blue-200 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
            </div>
            
            {/* The tesseract is absolutely positioned inside this block, so it
                contributes NO height. Given its own row it pushed the search box
                and everything under it down the page — the egg is meant to be
                found in the margin, not to move the furniture.

                Anchored right and centred on this line with top-1/2, so it stays
                level with the text at any font size rather than depending on a
                measured offset. */}
            {/* Two instances, one per breakpoint, because the desktop and mobile
                placements are different DOM positions rather than different CSS
                on the same node — and faking that with one node would need a
                hard-coded top offset, which is the fragile thing this markup
                already avoids once.

                Desktop: level with this line at the right edge of the text
                column, absolutely positioned so it adds no height (a row of its
                own pushed the whole page down — see d1f3aa7).

                Mobile: that position lands ON the copy, because the line wraps
                to two or three lines and fills the column. Below sm it gets its
                own slim row between the two text lines instead. That does add
                ~28px on mobile, which is the point — there is no free margin to
                sit in at that width. */}
            <div className="relative max-w-2xl mx-auto mb-4">
              <p className="text-lg text-slate-400 leading-relaxed">
                Mapping the narrative threads of consciousness
              </p>
              <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2 w-11">
                <Tesseract />
              </div>
            </div>

            {/* Mobile-only placement: its own row between the two lines.
                h-11 because the artwork is 44px and AmbientEgg's track carries
                that as an explicit height — a shorter row does not clip it, it
                just lets it bleed onto the line below. */}
            <div className="sm:hidden h-11 mt-1 mb-4">
              <Tesseract />
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Signal strength: Rising • Frequency: 432 Hz
            </p>
          </div>

          {/* Semantic Search */}
          <section
            aria-labelledby="semantic-search"
            className="max-w-4xl mx-auto mb-10"
          >
            <div className="rounded-xl border border-border bg-card p-6 shadow-2xl">
              <h2 id="semantic-search" className="text-lg font-medium text-foreground mb-4">
                Semantic Search
              </h2>

              <NaturalLanguageSearchBar
                onSearch={handleSearch}
                isSearching={isSearching}
                placeholder="Describe what you're looking for..."
              />

              {hasSearched && (
                <div className="mt-6 space-y-4">
                  <SearchFilters filters={searchFilters} onFiltersChange={setSearchFilters} />

                  <SearchResultsView
                    results={searchResults}
                    query={searchQuery}
                    isLoading={isSearching}
                    responseTimeMs={responseTimeMs}
                    onResultClick={handleResultClick}
                  />

                  {searchResults.length > 0 && (
                    <QuerySuggestions
                      currentQuery={searchQuery}
                      results={searchResults.map(r => ({ title: r.title, author: r.author }))}
                      onSuggestionClick={handleSearch}
                    />
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Navigation Links */}
          <div className="flex flex-col space-y-8 max-w-4xl mx-auto">
            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/library" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-blue-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-blue-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <BookOpen className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Transmissions</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Your saved signal archive
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/book-browser" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-blue-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-blue-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Search className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Signal Archive</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Discover new sci-fi signals
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/author-matrix" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-blue-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-blue-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Eye className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Author Matrix</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Explore author consciousness maps
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/insights" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-blue-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-blue-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Brain className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Reading Insights</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    AI-powered reading analysis
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/publisher-resonance" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-purple-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-purple-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
                  <Building className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Publisher Resonance</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Explore publisher consciousness
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/test-brain" 
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-cyan-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-cyan-500/10 flex items-center space-x-6 w-full"
              >
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                  <Brain className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Neural Map</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Neural consciousness visualization
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/community"
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-emerald-500/10 flex items-center space-x-6 w-full text-left"
              >
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Community</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Connect with other readers
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/protagonists"
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-violet-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-violet-500/10 flex items-center space-x-6 w-full text-left"
              >
                <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center group-hover:bg-violet-500/30 transition-colors">
                  <MessageCircle className="w-6 h-6 text-violet-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Speak to Protagonist</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    Chat with characters from your library
                  </p>
                </div>
              </Link>
            </div>

            <div ref={addFeatureBlockRef} className="feature-block">
              <Link 
                to="/book-to-screen"
                className="group bg-slate-800/30 border border-slate-700/50 rounded-lg p-8 hover:bg-slate-800/50 hover:border-amber-500/30 transition-all duration-500 hover:shadow-lg hover:shadow-amber-500/10 flex items-center space-x-6 w-full text-left"
              >
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                  <Film className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-medium text-slate-200 mb-2">Book to Screen</h2>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    SF film adaptations with trailers
                  </p>
                </div>
              </Link>
            </div>

          </div>

          <div className="max-w-4xl mx-auto mt-16">
            <div className="flex flex-col md:grid md:grid-cols-3 items-center text-slate-400 text-xs gap-4 md:gap-4">
              <div className="flex items-center justify-center md:justify-start gap-2 w-full md:w-auto">
                <div className="scale-75 origin-center md:origin-left">
                  <NewsletterSignup />
                </div>
              </div>
              
              <div className="flex items-center justify-center w-full md:w-auto">
                <span aria-hidden="true" className="mr-2">•</span>
                <span className="inline-flex items-center space-x-2">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse inline-block" />
                  <span className="whitespace-nowrap">Quantum resonance stabilized</span>
                </span>
                <span aria-hidden="true" className="ml-2">•</span>
              </div>
              
              <div className="flex items-center justify-center md:justify-end gap-2 w-full md:w-auto flex-wrap md:flex-nowrap">
                <StandardButton
                  onClick={() => setShowContactModal(true)}
                  variant="standard"
                  size="xs"
                  className="inline-flex items-center justify-center gap-1.5 px-3 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 ring-0 outline-none whitespace-nowrap min-w-[120px]"
                >
                  <Mail className="w-3 h-3 flex-shrink-0" />
                  <span>Make Contact</span>
                </StandardButton>
                
                <ContributionButton 
                  walletAddress="0xDd966928cC8EA2e61221170490D696BD224866bf"
                />
              </div>
            </div>
          </div>
        </main>

        <ContactModal
          isOpen={showContactModal} 
          onClose={() => setShowContactModal(false)} 
        />
        <CommunityModal
          isOpen={showCommunityModal}
          onClose={() => setShowCommunityModal(false)}
        />
        <ReadingVibeOverlay />
      </div>
    </>
  );
};

export default Discovery;
